/**
 * テーマカラー - choosing the colour the application is drawn in.
 *
 * One colour, and the whole theme follows from it (see theme.ts and the :root
 * note in styles.css): buttons, tints, lines, shadows, the deep brand panels,
 * even the neutrals' faint tint. What is chosen here is a SEED, not a paint.
 *
 * The preview is the real components - .btn, .pill, .chip, a card tint - inside
 * a `.theme-scope` carrying the draft's two numbers, so it shows exactly what
 * the app will look like without repainting the screen it sits on (a JMC
 * operator choosing a clinic's colour should not see the JMC workspace turn
 * that colour while they decide).
 */
import { useEffect, useState } from 'react';
import { useRevalidator } from 'react-router';
import { ApiError, put } from '../api.ts';
import { Card, Pill } from './ui.tsx';
import { ColorPicker } from './ColorPicker.tsx';
import {
  DEFAULT_BRAND,
  HEX,
  THEME_PRESETS,
  contrastOnBrand,
  themeStyle,
} from '../theme.ts';

export function ThemePicker({
  endpoint,
  value,
  inherited,
  canEdit = true,
  caption = '選んだ色をもとに、ボタン・背景・線・影などアプリ全体の配色を自動で組み立てます。どの色を選んでも文字が読みやすいよう、明るさは自動で調整されます。',
}: {
  /** Replaces the default explanation, e.g. for an operator's own colour. */
  caption?: string;
  /** PUT target: /clinic/theme, /stores/:id/theme, or their /admin twins. */
  endpoint: string;
  /** The colour saved here, or null for "inherit". */
  value: string | null;
  /** What null falls back to - a store inherits its clinic's colour. */
  inherited?: { label: string; hex: string | null };
  canEdit?: boolean;
}) {
  const revalidator = useRevalidator();
  const [draft, setDraft] = useState<string>(value ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setDraft(value ?? '');
  }, [value]);

  const fallback = inherited?.hex ?? DEFAULT_BRAND;
  const shown = HEX.test(draft) ? draft : fallback;
  const changed = (HEX.test(draft) ? draft.toLowerCase() : null) !== (value ?? null);

  function choose(hex: string) {
    setDraft(hex);
    setError(null);
  }

  async function save(next: string | null) {
    setSaving(true);
    setError(null);
    try {
      await put(endpoint, { brandColor: next });
      // The session carries the resolved colour; re-reading it re-themes the
      // app when this was the viewer's own clinic or store.
      await revalidator.revalidate();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '保存できませんでした');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card
      title="テーマカラー"
      caption={caption}
    >
      <div className="themepick">
        <div className="themepick__controls">
          <div className="themepick__swatches" role="group" aria-label="おすすめの色">
            {THEME_PRESETS.map((preset) => (
              <button
                key={preset.hex}
                type="button"
                className="themepick__swatch"
                style={{ background: preset.hex }}
                title={preset.label}
                aria-label={preset.label}
                aria-pressed={shown.toLowerCase() === preset.hex}
                disabled={!canEdit}
                onClick={() => choose(preset.hex)}
              />
            ))}
          </div>

          {/*
            The full picker: square, hue, eyedropper, hex and RGB. The presets
            above are the quick answer; this is for matching a clinic's exact
            brand colour, often picked straight off their website or logo.
          */}
          <ColorPicker idPrefix={endpoint} value={shown} onChange={choose} disabled={!canEdit} />
          <span className="field__hint" style={{ margin: 0 }}>
            {value
              ? `いまの設定：${value}`
              : `いまの設定：${inherited ? `${inherited.label}（${fallback}）` : '既定の色'}`}
          </span>

          {error && (
            <p className="alert small" role="alert" style={{ margin: 0 }}>
              {error}
            </p>
          )}

          {canEdit && (
            <div className="editpanel__actions" style={{ marginTop: 0 }}>
              <button
                className="btn btn--sm"
                type="button"
                disabled={saving || !changed || !HEX.test(draft)}
                onClick={() => void save(draft.toLowerCase())}
              >
                {saving ? '保存中…' : 'この色にする'}
              </button>
              <button
                className="btn btn--ghost btn--sm"
                type="button"
                disabled={saving || value === null}
                onClick={() => void save(null)}
              >
                {inherited ? `${inherited.label}に戻す` : '既定の色に戻す'}
              </button>
            </div>
          )}
        </div>

        {/*
          The real components, themed by the draft. A .theme-scope re-derives
          every token from its own --brand-h / --brand-c; see styles.css.
        */}
        <div className="themepick__preview theme-scope" style={themeStyle(shown)} aria-hidden>
          <div className="themepick__bar">記事咲</div>
          <div className="themepick__body">
            <p className="themepick__title">プレビュー</p>
            <div className="chips">
              <button type="button" className="btn btn--sm" tabIndex={-1}>
                記事を作成
              </button>
              <button type="button" className="btn btn--ghost btn--sm" tabIndex={-1}>
                キャンセル
              </button>
            </div>
            <div className="chips">
              <span className="chip" aria-pressed="true">
                選択中
              </span>
              <span className="chip">未選択</span>
              <Pill>クリニック共通</Pill>
            </div>
            <p className="themepick__text">
              本文の文字と<a href="#preview">リンク</a>の見え方です。
            </p>
            <p className="muted small" style={{ margin: 0 }}>
              白文字のコントラスト {contrastOnBrand(shown)}:1
            </p>
          </div>
        </div>
      </div>
    </Card>
  );
}
